import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

type Props = {
  open: boolean
  memberEmail: string
  onClose: () => void
  children: React.ReactNode
}

/**
 * Mobile-only bottom sheet for team member permissions (md+ callers should not render).
 * Handles backdrop, enter animation, body scroll lock, and Escape to close.
 */
export default function MemberPermissionsBottomSheet({ open, memberEmail, onClose, children }: Props) {
  const [entered, setEntered] = useState(false)

  useEffect(() => {
    if (!open) {
      setEntered(false)
      return
    }
    const id = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(id)
  }, [open])

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div
      className="tw-pointer-events-none tw-fixed tw-inset-0 tw-z-[1050] tw-flex tw-flex-col tw-justify-end md:tw-hidden"
      aria-modal="true"
      role="dialog"
      aria-labelledby="member-permissions-sheet-title"
    >
      <button
        type="button"
        className={`tw-pointer-events-auto tw-absolute tw-inset-0 tw-bg-black/40 tw-transition-opacity tw-duration-300 tw-ease-out ${
          entered ? 'tw-opacity-100' : 'tw-opacity-0'
        }`}
        onClick={onClose}
        aria-label="Close permissions"
      />
      <div
        className={`tw-pointer-events-auto tw-relative tw-z-[1] tw-flex tw-h-[80vh] tw-max-h-[80vh] tw-w-full tw-max-w-full tw-flex-col tw-overflow-hidden tw-rounded-t-2xl tw-bg-white tw-shadow-2xl tw-transition-transform tw-duration-300 tw-ease-out ${
          entered ? 'tw-translate-y-0' : 'tw-translate-y-full'
        }`}
      >
        <div className="tw-flex tw-shrink-0 tw-flex-col tw-px-4 tw-pt-3">
          <div className="tw-mb-3 tw-flex tw-justify-center" aria-hidden>
            <div className="tw-h-1 tw-w-10 tw-rounded-full tw-bg-slate-300" />
          </div>
          <div className="tw-relative tw-flex tw-items-start tw-justify-between tw-gap-3 tw-pb-2">
            <div id="member-permissions-sheet-title" className="tw-min-w-0 tw-pr-12">
              <p className="tw-mb-0.5 tw-text-xs tw-font-medium tw-text-slate-500">Permissions</p>
              <p className="tw-mb-0 tw-break-all tw-text-sm tw-font-semibold tw-text-slate-900">{memberEmail}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="tw-absolute tw-right-0 tw-top-0 tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-text-slate-600 tw-transition-colors hover:tw-bg-slate-100 focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-slate-400/40"
              aria-label="Close"
            >
              <X className="tw-h-5 tw-w-5" strokeWidth={2} />
            </button>
          </div>
        </div>
        <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-px-4">{children}</div>
      </div>
    </div>,
    document.body
  )
}
